import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { firstValueFrom, switchMap } from 'rxjs';

import { TicketsDataService } from '@core/ask-hr-tickets-logic';
import { type AnyChatComponentProvider, provideChatComponents } from '@core/intelligence-smart-ui';

import { AskTicketItemComponent } from '../../ask-ticket-item/ask-ticket-item.component';
import { TicketDialogService } from '../../ticket-dialog.service';

interface RequestTicketChatComponentConfig {
  name: 'request-ticket';
  props: {
    id: number;
  };
}

@Component({
  selector: 'ask-chat-request-item-dynamic',
  imports: [AskTicketItemComponent],
  template: `
    @if (ticket(); as ticketValue) {
      <ask-ticket-item [ticket]="ticketValue" (click)="onClick()" layout-full-width />
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatTicketItemDynamicComponent {
  private readonly ticketsDataService = inject(TicketsDataService);
  private readonly ticketDialogService = inject(TicketDialogService);

  public props = input.required<RequestTicketChatComponentConfig['props']>();

  protected ticketId = computed(() => this.props().id);
  protected ticket = toSignal(
    toObservable(this.ticketId).pipe(switchMap(id => this.ticketsDataService.getTicketById$(id))),
    { initialValue: null },
  );

  private loadTicket = effect(() =>
    firstValueFrom(this.ticketsDataService.loadTicketById(this.props().id)),
  );

  protected onClick() {
    this.ticketDialogService.openTicketDialog(this.props().id, { routerStrategy: false });
  }
}

export const provideAssistantChatTicketItem = provideChatComponents({
  useValue: {
    name: 'request-ticket',
    component: ChatTicketItemDynamicComponent,
  } satisfies AnyChatComponentProvider<RequestTicketChatComponentConfig>,
});
